import type { SearchSchemaInput } from '@tanstack/react-router';

import type { SearchEntityType } from '../../lib/dto';
import { SEARCH_TYPE_ORDER } from './labels';

/**
 * حالة شاشة البحث تعيش في search params (§1.2: «q في search params»، و§1.6:
 * FilterChips تُزامَن مع search params) — الرابط قابل للمشاركة والرجوع بالتاريخ
 * يحفظ آخر بحث. هذا الملف هو المترجم الوحيد بين الـ URL وفلاتر الاستعلام.
 */

export type SearchTypeId = 'all' | SearchEntityType;

export interface SearchScreenSearch {
  readonly q: string;
  readonly type: SearchTypeId;
  /** معرّف المظلة — `''` = كل المظلات. */
  readonly venture: string;
}

export const DEFAULT_SEARCH: SearchScreenSearch = { q: '', type: 'all', venture: '' };

const TYPE_IDS: readonly SearchTypeId[] = ['all', ...SEARCH_TYPE_ORDER];

function asTypeId(value: unknown): SearchTypeId {
  return TYPE_IDS.find((id) => id === value) ?? 'all';
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * دخل مختلف عن الخرج بعلامة `SearchSchemaInput` (نفس نمط `projects/list-filters.ts`):
 * الخرج كامل الحقول فيقرأه المكوّن بلا فحوص، والدخل كله اختياري فيبقى
 * `<Link to="/search">` في الشريط الجانبي بلا search params.
 */
export type SearchScreenSearchInput = {
  readonly q?: unknown;
  readonly type?: unknown;
  readonly venture?: unknown;
} & SearchSchemaInput;

/** يتسامح مع أي قيمة في الـ URL: فلتر مجهول يعود إلى الافتراضي بلا شاشة خطأ. */
export function validateSearchScreenSearch(input: SearchScreenSearchInput): SearchScreenSearch {
  return {
    q: asText(input.q),
    type: asTypeId(input.type),
    venture: asText(input.venture),
  };
}

/** ما يُكتب في الـ URL فعلًا — بلا علامة `SearchSchemaInput` (فهي للدخل المُتحقَّق منه). */
export interface SearchScreenSearchWrite {
  readonly q?: string | undefined;
  readonly type?: SearchTypeId | undefined;
  readonly venture?: string | undefined;
}

/**
 * الكتابة إلى الـ URL: القيم الافتراضية تُحذف (`undefined`) لا تُكتب فارغة — فيبقى
 * `/search` نظيفًا و`/search?q=فكرة` هو أقصر رابط يعبّر عن الحالة.
 */
export function toSearchInput(state: SearchScreenSearch): SearchScreenSearchWrite {
  return {
    q: state.q === '' ? undefined : state.q,
    type: state.type === 'all' ? undefined : state.type,
    venture: state.venture === '' ? undefined : state.venture,
  };
}

/** أنواع الاستعلام المرسلة إلى `GET /search?types=…` — الافتراضي بلا فلتر. */
export function typesFor(type: SearchTypeId): readonly SearchEntityType[] | undefined {
  return type === 'all' ? undefined : [type];
}
